import {
  type RefObject,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
} from 'react';

import { isInsideFloatingOverlay } from '@/lib/base/overlays/floating-overlay';

import { isPopoverOpenWithin } from './isPopoverOpenWithin';

/**
 * Whether focus has GENUINELY entered the popover opened within `container`
 * — not merely because `aria-expanded` flipped true. During the opening
 * window (popover open, focus not yet inside it) this is false, so callers
 * keep focus enforcement active rather than standing down early.
 */
function hasFocusEnteredPopover(
  container: HTMLElement,
  target: Node | null
): boolean {
  return (
    isPopoverOpenWithin(container) &&
    isInsideFloatingOverlay(target, { surface: container })
  );
}

/**
 * Keeps a row's inline editor focused and open while its picker popover
 * (stage, association, select, date…) settles, and treats a genuine Escape
 * dismissal of that popover as the user's signal that they're done editing.
 *
 * Three cooperating effects:
 *  1. Move focus into the editor when editing starts. Without this the
 *     active element stays wherever it was (often <body> after the trigger
 *     unmounts), so typing needs a second click and Escape reaches the
 *     surrounding slideout — closing it instead of discarding the edit.
 *     Skipped when focus already landed inside. A picker that auto-opens its
 *     (portaled) popover still gets its TRIGGER focused here: react-aria
 *     moves focus into the menu only once the popover has entered (a few
 *     hundred ms), and in that window an Escape from <body> would close the
 *     slideout — from the trigger it reaches the container's own handler
 *     and discards the edit instead.
 *  2. …and keep it there while the editor settles. Two ways focus falls to
 *     <body> mid-edit through no act of the user:
 *      - An auto-opened picker's popover mounts before the effect above runs
 *        (child effects first), so its FocusScope records <body> as the
 *        element to restore to; the popover then remounts once its list is
 *        ready and dutifully "restores" focus to <body>, ~300ms before the
 *        new instance focuses its menu. That one fires `focusout` with no
 *        relatedTarget.
 *      - A multi-value editor re-renders its tags as their objects resolve;
 *        a focused element that is REMOVED loses focus with no event at all,
 *        so that case is caught by watching the editor's subtree instead.
 *     An Escape in either gap reaches the slideout and closes it. Whenever
 *     focus is on <body> mid-edit, put it back on the editor. Skipped once a
 *     commit is in flight (`isSaving`): a click-away that started the save has
 *     intentionally moved focus to the clicked page target, and because the
 *     save is async this editor stays mounted (a disabled control) until it
 *     resolves — rehoming would steal that focus back. useInlineEdit refocuses
 *     the trigger when the save resolves, so there is nothing to keep here.
 *  3. An Escape observer (capture phase) that calls `cancelEdit` when the
 *     user dismisses the picker popover via keyboard. It runs only while
 *     editing, and only when focus is outside the row's container (i.e., the
 *     picker popover is open and the row's own onKeyDown has not seen the key).
 *     Click-away dismissal is already handled by useInlineEdit's
 *     handlePointerDownOutside; picking a value is handled by setDraft — so
 *     Escape is the only additional signal this effect needs to observe.
 */
export function usePickerOpenGuard(
  containerRef: RefObject<HTMLDivElement | null>,
  isEditing: boolean,
  isSaving: boolean,
  focusEditor: (container: HTMLElement) => void,
  cancelEdit: () => void,
  isSavingNow: () => boolean
): void {
  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }
    const container = containerRef.current;
    if (!container || container.contains(document.activeElement)) {
      return;
    }
    if (hasFocusEnteredPopover(container, document.activeElement)) {
      return;
    }
    focusEditor(container);
  }, [isEditing, containerRef, focusEditor]);

  useEffect(() => {
    // Stand down once a commit is in flight: an outside click that started the
    // async save deliberately moved focus to the clicked page target, and the
    // editor stays mounted (disabled) until the save resolves — rehoming here
    // would yank that focus back. See effect 2's note in the block comment.
    //
    // The top-level `isSaving` gate here handles the common case, but there is
    // a race: a MutationObserver or focusout handler can queue the rehome
    // microtask BEFORE `setStatus('saving')` has caused a re-render — so the
    // effect's closure still sees the stale `isSaving=false` and would fire
    // `focusEditor`. To close that window, `rehome()` re-reads the SYNCHRONOUSLY-
    // FRESH `isSavingNow()` inside the queued microtask itself. `isSavingNow`
    // reads `isSavingRef.current`, which commitImpl sets to `true` BEFORE it
    // calls `setStatus('saving')`, so any save that started after the microtask
    // was queued but before it runs is still caught.
    if (!isEditing || isSaving) {
      return undefined;
    }
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const rehome = () => {
      queueMicrotask(() => {
        if (
          !container.isConnected ||
          document.activeElement !== document.body
        ) {
          return;
        }
        if (hasFocusEnteredPopover(container, document.activeElement)) {
          return;
        }
        // Re-check saving state synchronously: a save may have started after
        // this microtask was queued but before it runs (before React re-renders
        // with isSaving=true). isSavingNow() reads the ref that commitImpl sets
        // before any await, so it is always current.
        if (isSavingNow()) {
          return;
        }
        focusEditor(container);
      });
    };
    const onFocusOut = (e: FocusEvent) => {
      if (e.relatedTarget === null) {
        rehome();
      }
    };
    document.addEventListener('focusout', onFocusOut, true);
    const observer = new MutationObserver(rehome);
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      document.removeEventListener('focusout', onFocusOut, true);
      observer.disconnect();
    };
  }, [isEditing, isSaving, containerRef, focusEditor, isSavingNow]);

  // Escape observer: fires when the user presses Escape while the picker
  // popover is open (focus is outside the row container). This is the only
  // positive dismissal signal this guard acts on — click-away and value-pick
  // are already handled elsewhere. Installed in capture phase so it runs
  // before react-aria's popover handler and the slideout's document keydown;
  // stopImmediatePropagation prevents both from also firing.
  const handleKeyDown = useEffectEvent((e: KeyboardEvent) => {
    if (e.key !== 'Escape') {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    // Focus is inside the row — the container's own onKeyDown/handleKeyDown
    // in useInlineEdit handles Escape. Do NOT double-handle.
    if (container.contains(document.activeElement)) {
      return;
    }
    // Ignore Escape while a commit is in flight (mirrors useInlineEdit's
    // handleKeyDown, which returns early when saving): a second Escape must not
    // cancel — and thereby reset — an active async save. Also skip when the
    // picker is not open (e.g. a nested "Create new …" slideout is open and its
    // Escape must close IT, not discard this row).
    //
    // Reads isSavingNow() rather than the closure-captured `isSaving` param —
    // this is an Effect Event, so a save that started after the listener was
    // attached but before a re-render would otherwise still see a stale
    // `isSaving=false`. Same fresh-read rationale as rehome() above.
    if (isSavingNow() || !isPopoverOpenWithin(container)) {
      return;
    }
    // Genuine picker dismissal: cancel the edit.
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    cancelEdit();
  });

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    const onKeyDown = (e: KeyboardEvent) => handleKeyDown(e);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
    // handleKeyDown is an Effect Event — never a dependency (REACT_19_PATTERNS § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);
}
